/**
 *
 * Copyright © 2017 Easymeeting AS. All rights reserved.
 *
 * This software is produced by Easymeeting AS.
 * Unauthorized redistribution, reproduction, or usage of
 * this software in whole or in part without the express
 * written consent of Easymeeting AS is strictly prohibited.
 */

import { TAB_KEYS } from '@/helpers/constants';

export default {
  namespaced: true,
  state: {
    activeTab: TAB_KEYS.FAVORITES,
    bandwidth: 1024,
    cameraName: null,
    config: null,
    controlsConnected: [],
    deviceInfo: null,
    freeclientRoom: null,
    freeclientName: null,
    inMeeting: false,
    inPhoneCall: false,
    isDesktopApp: false,
    loudSpeakerEnabled: false,
    meetingData: null,
    micMuted: false,
    orientation: 'landscape',
    pipEnabled: true,
    shareStarted: false,
    speakerMuted: false,
    videoEnabled: true,
    selectedIODevices: {
      audioInput: null,
      audioOutput: null,
      videoInput: null,
      remember: false
    }
  },
  getters: {
    activeTab (state) {
      return state.activeTab;
    },
    bandwidth (state) {
      return state.bandwidth;
    },
    cameraName (state) {
      return state.cameraName;
    },
    config (state) {
      return state.config;
    },
    controlsConnected (state) {
      return state.controlsConnected;
    },
    countries (state) {
      return state.config && state.config.countries
        ? state.config.countries
        : [];
    },
    deviceInfo (state) {
      return state.deviceInfo;
    },
    freeclientRoom (state) {
      return state.freeclientRoom;
    },
    freeclientName (state) {
      return state.freeclientName;
    },
    inMeeting (state) {
      return state.inMeeting;
    },
    inPhoneCall (state) {
      return state.inPhoneCall;
    },
    isDesktopApp (state) {
      return state.isDesktopApp;
    },
    isOauthEnabled (state) {
      return state.config.appconfig.EXTERNAL_AUTH &&
      state.config.appconfig.OAUTH_CONFIG.AUTH_METHOD === 'OAUTH2';
    },
    loudSpeakerEnabled (state) {
      return state.loudSpeakerEnabled;
    },
    meetingData (state) {
      return state.meetingData;
    },
    micMuted (state) {
      return state.micMuted;
    },
    orientation (state) {
      return state.orientation;
    },
    pipEnabled (state) {
      return state.pipEnabled;
    },
    shareStarted (state) {
      return state.shareStarted;
    },
    speakerMuted (state) {
      return state.speakerMuted;
    },
    videoEnabled (state) {
      return state.videoEnabled;
    },
    selectedIODevices (state) {
      return state.selectedIODevices;
    }
  },
  mutations: {
    activeTab (state, tabName) {
      state.activeTab = tabName;
    },
    bandwidth (state, bandwidth) {
      state.bandwidth = bandwidth;
    },
    isInMeeting (state, status) {
      state.inMeeting = status;
    },
    isInPhoneCall (state, status) {
      state.inPhoneCall = status;
    },
    loudSpeakerEnabled (state, status) {
      state.loudSpeakerEnabled = status;
    },
    micMuted (state, status) {
      state.micMuted = status;
    },
    pipEnabled (state, status) {
      state.pipEnabled = status;
    },
    removeControls (state, controls) {
      controls.forEach(control => {
        const index = state.controlsConnected.findIndex(elem => {
          return elem.id === control.id;
        });
        if (index >= 0) {
          state.controlsConnected.splice(index, 1);
        }
      });
    },
    setCameraName (state, cameraName) {
      state.cameraName = cameraName;
    },
    setConfig (state, config) {
      state.config = config;
    },
    setControls (state, controls) {
      state.controlsConnected = controls;
    },
    setDeviceInfo (state, deviceInfo) {
      state.deviceInfo = deviceInfo;
    },
    setFreeClientInfo (state, { name, room }) {
      state.freeclientName = name;
      state.freeclientRoom = room;
    },
    setIsDesktopApp (state, status) {
      state.isDesktopApp = !!status;
    },
    setMeetingData (state, meetingData) {
      state.meetingData = meetingData;
    },
    setOrientation (state, orientation) {
      state.orientation = orientation;
    },
    setSelectedIODevices (state, ioDevices) {
      state.selectedIODevices = ioDevices;
    },
    shareStarted (state, status) {
      state.shareStarted = status;
    },
    speakerMuted (state, status) {
      state.speakerMuted = status;
    },
    updateConfig (state, config) {
      state.config = { ...state.config, ...config };
    },
    updateDeviceInfo (state, deviceInfo) {
      state.deviceInfo = { ...state.deviceInfo, ...deviceInfo };
    },
    videoEnabled (state, status) {
      state.videoEnabled = status;
    }
  },
  actions: {
    removeControls ({ commit }, controls) {
      commit('removeControls', controls);
    },
    setActiveTab ({ commit }, tabName) {
      commit('activeTab', tabName);
    },
    setBandwidth ({ commit }, bandwidth) {
      commit('bandwidth', bandwidth);
    },
    setCameraName ({ commit }, cameraName) {
      commit('setCameraName', cameraName);
    },
    setConfig ({ commit }, config) {
      commit('setConfig', config);
    },
    setControls ({ commit }, controls) {
      commit('setControls', controls);
    },
    setDeviceInfo ({ commit }, deviceInfo) {
      commit('setDeviceInfo', deviceInfo);
    },
    setFreeClientInfo ({ commit }, { name, room }) {
      commit('setFreeClientInfo', { name, room });
    },
    setInMeetingStatus ({ commit }, status) {
      commit('isInMeeting', status);
    },
    setInPhoneCallStatus ({ commit }, status) {
      commit('isInPhoneCall', status);
    },
    setIsDesktopApp ({ commit }, status) {
      commit('setIsDesktopApp', status);
    },
    setLoudSpeakerEnabled ({ commit }, status) {
      commit('loudSpeakerEnabled', status);
    },
    setMeetingData ({ commit }, meetingData) {
      commit('setMeetingData', meetingData);
    },
    setMicMuted ({ commit }, status) {
      commit('micMuted', status);
    },
    setOrientation ({ commit }, orientation) {
      commit('setOrientation', orientation);
    },
    setPipEnabled ({ commit }, status) {
      commit('pipEnabled', status);
    },
    setShareStarted ({ commit }, status) {
      commit('shareStarted', status);
    },
    setSpeakerMuted ({ commit }, status) {
      commit('speakerMuted', status);
    },
    setSelectedIODevices ({ commit }, ioDevices) {
      commit('setSelectedIODevices', ioDevices);
    },
    updateConfig ({ commit }, config) {
      commit('updateConfig', config);
    },
    updateDeviceInfo ({ commit }, deviceInfo) {
      commit('updateDeviceInfo', deviceInfo);
    },
    setVideoEnabled ({ commit }, status) {
      commit('videoEnabled', status);
    }
  }
};
